import { createContext, type Dispatch, type SetStateAction, useCallback, useContext, useEffect, useState } from "react";

type NavigationValue = string | number | boolean | undefined;
type NavigationStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

// Navigation is private to this browser tab and workspace. Never put record
// contents, drafts, or credentials here; only destination IDs and view choices.
export function createNavigationSession(scope: string | undefined) {
  return { scope, restoring: true, consumed: new Set<string>() };
}
export const WorkspaceNavigationContext = createContext<ReturnType<typeof createNavigationSession> | undefined>(
  undefined,
);

export const navigationId = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length <= 512 &&
  Array.from(value).every((character) => character.charCodeAt(0) >= 32);
export const navigationBoolean = (value: unknown): value is boolean => typeof value === "boolean";
export const navigationInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value);
export const navigationDate = (value: unknown): value is string =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(`${value}T12:00:00Z`)) &&
  new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
export const navigationChoice =
  <const T extends string>(values: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === "string" && values.includes(value as T);

function storageKey(scope: string, key: string) {
  return `classroom-navigation-v1:${JSON.stringify([scope, key])}`;
}

export function readNavigationState<T extends NavigationValue>(
  storage: NavigationStorage | undefined,
  scope: string | undefined,
  key: string,
  fallback: T,
  accepts: (value: unknown) => value is T,
): T {
  if (!scope || !storage) return fallback;
  try {
    const raw = storage.getItem(storageKey(scope, key));
    if (raw === null) return fallback;
    const value: unknown = JSON.parse(raw);
    return ["string", "number", "boolean"].includes(typeof value) && accepts(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

export function writeNavigationState(
  storage: NavigationStorage | undefined,
  scope: string | undefined,
  key: string,
  value: NavigationValue,
) {
  if (!scope || !storage) return;
  try {
    if (value === undefined) storage.removeItem(storageKey(scope, key));
    else storage.setItem(storageKey(scope, key), JSON.stringify(value));
  } catch {
    // A browser that denies sessionStorage can still navigate normally.
  }
}

function sessionStorageOrUndefined() {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function useNavigationState<T extends NavigationValue>(
  key: string,
  initial: T | (() => T),
  accepts: (value: unknown) => value is T,
): [T, Dispatch<SetStateAction<T>>] {
  const session = useContext(WorkspaceNavigationContext);
  const scope = session?.scope;
  const identity = JSON.stringify([scope, key]);
  const read = () => {
    const fallback = typeof initial === "function" ? initial() : initial;
    if (!session?.restoring || session.consumed.has(identity)) return fallback;
    return readNavigationState(sessionStorageOrUndefined(), scope, key, fallback, accepts);
  };
  const [state, setState] = useState(() => ({ identity, value: read() }));
  // Feature components can stay mounted while their class changes. Restore the
  // new class before effects can write the previous class's destination into it.
  if (state.identity !== identity) setState({ identity, value: read() });
  useEffect(() => {
    if (state.identity === identity) {
      session?.consumed.add(identity);
      writeNavigationState(sessionStorageOrUndefined(), scope, key, state.value);
    }
  }, [identity, key, scope, state, session]);
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (next) =>
      setState((current) => {
        const value = typeof next === "function" ? next(current.value) : next;
        return current.identity === identity && Object.is(current.value, value) ? current : { identity, value };
      }),
    [identity],
  );
  return [state.value, setValue];
}
